import { produce } from 'immer'
import { map as _map, pick as _pick } from 'lodash'
import { isEqual as _isEqual, uniq as _uniq } from 'lodash/fp'
import { v4 as uuidv4 } from 'uuid'

import {
  ActionAgent,
  ActionTarget,
  AddThreadMessageRequest,
  ContextItem,
  Conversation,
  ConversationMessageHint,
  ConversationThread,
  ConversationThreadExecutionPlanActionType,
  ConversationThreadMessageState,
  ConversationThreadState,
  ExecutionPlan,
  ExecutionPlanAction,
  Message,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { deepPruneNull } from '@flowx/designer/shared/entities/utils'
import { ComponentIdentifierEnum, TemplateConfigInterface } from '@flowx/shared/interfaces'

import { AgentAction, AgentName } from './ai-conversation.commons'
import { Action, ActionsDiff, SerializedSelectedActions } from './ai-conversation.dictionary'
import { ContextDataKeys, OutcomeType } from './ai-conversation.model'
import { ConversationScope } from './conversation.constants'

// Only rejects when both sides are populated and disagree: a global page has no appId, and older
// conversations may carry empty scope fields.
export const isConversationInScope = (
  conversation: Conversation,
  { workspaceId, appId }: ConversationScope
): boolean =>
  (!workspaceId || !conversation.workspaceId || conversation.workspaceId === workspaceId) &&
  (!appId || !conversation.applicationId || conversation.applicationId === appId)

export const isUnfinishedMessageWithValidExecutionPlan = (message: Message): boolean =>
  message.state !== ConversationThreadMessageState.Finished && messageHasValidExecutionPlan(message)

const FINISHED_THREAD_STATES: ConversationThreadState[] = [
  ConversationThreadState.Kept,
  ConversationThreadState.Discarded,
]

export const isFinishedThreadState = (state: ConversationThreadState | undefined): boolean =>
  FINISHED_THREAD_STATES.includes(state)

export function buildNoopStoreAction(): ExecutionPlanAction {
  return {
    actionType: ConversationThreadExecutionPlanActionType.Automatic,
    agent: {
      action: AgentAction.NoopStore,
      agent: AgentName.ConnectedGraph,
    },
    localId: uuidv4(),
    context: { items: [] },
    description: '',
    name: '',
    parallel: false,
    target: {
      name: null,
      targetType: null,
    },
  }
}

export function buildNoopStoreHint(): ConversationMessageHint {
  return {
    autoStart: false,
    actions: [buildNoopStoreAction()],
  }
}

export function buildConversationThreadSystemMessageInput(
  message: string
): AddThreadMessageRequest {
  return {
    message,
    documents: [],
    contextData: { items: [] },
    hint: buildNoopStoreHint(),
  }
}

export function buildSystemMessageFromConversationThreadMessage(
  message: Message,
  enhanceExecutionPlanDiffEmpty = false
): string {
  if (isEmptyExecutionPlan(message.executionPlan) || enhanceExecutionPlanDiffEmpty) {
    return `I'll need a bit more detail before I can help with this. Could you please provide some additional information?`
  }

  if (isUnavailableExecutionPlan(message.executionPlan)) {
    return 'I can not help you with that right now - I will be able to answer questions about FlowX AI soon.'
  }

  return `Here's the plan I created. If you'd like adjustments, let me know. Once you're ready, Choose the task to implement.`
}
/**
 * Builds message outcome ID from base message ID
 */
export const buildMessageOutcomeId = (messageId: string): string => `${messageId}-outcome`

/**
 * Builds summary message ID from base message ID
 */
export const buildMessageSummaryId = (messageId: string): string => `${messageId}-summary`

/**
 * Whether a chat message id is the source message itself or one of its derived
 * outcome/summary messages
 */
export const isMessageDerivedFrom = (
  messageId: string | undefined,
  sourceMessageId: string
): boolean =>
  [
    sourceMessageId,
    buildMessageOutcomeId(sourceMessageId),
    buildMessageSummaryId(sourceMessageId),
  ].includes(messageId)

/**
 * Parses the JSON payload of a keyed context item, returning null when the item
 * is absent or its content is not valid JSON
 */
export const parseContextItemsJson = <T>(
  contextItems: Record<string, ContextItem> | undefined,
  key: string
): T | null => {
  const rawValue = contextItems?.[key]?.valueGenericString
  if (!rawValue) {
    return null
  }

  try {
    return JSON.parse(rawValue)
  } catch (error) {
    console.error(`Failed to parse context item ${key}:`, error)
    return null
  }
}

export function isEmptyExecutionPlan(executionPlan: ExecutionPlan): boolean {
  return !executionPlan?.actions?.length
}

export function hasNonAssistantActions(executionPlan: ExecutionPlan): boolean {
  return (executionPlan?.actions || []).some(
    (action) => action.agent?.agent !== AgentName.AiAssistant
  )
}

export function isUnavailableExecutionPlan(executionPlan: ExecutionPlan): boolean {
  return (executionPlan?.actions || []).some(
    (action) => action.agent.action === AgentAction.NotAvailableAgent
  )
}

export function isNoopExecutionPlan(executionPlan: ExecutionPlan): boolean {
  return (executionPlan?.actions || []).some(
    (action) => action.agent.action === AgentAction.NoopStore
  )
}

export const mapTemplateConfigToAiContext = (templateConfig: TemplateConfigInterface): any => {
  let contextData = {
    id: templateConfig.id,
    flowxUuid: templateConfig.flowxUuid,
    key: templateConfig.key,
    nodeDefinitionId: templateConfig.nodeDefinitionId,
    componentIdentifier: templateConfig.componentIdentifier,
    uiTemplateParentId: templateConfig.uiTemplateParentId,
    platformsDisplayOptions: _map(templateConfig.platformsDisplayOptions, (option) =>
      _pick(option, ['flowxProps.hide', 'flowxProps.disabled', 'flowxProps.label'])
    ).filter((option) => Object.keys(option).length > 0),
  }

  const extraContextData = componentMappers[templateConfig.componentIdentifier]
    ? componentMappers[templateConfig.componentIdentifier](templateConfig)
    : {}

  contextData = {
    ...contextData,
    ...extraContextData,
    platformsDisplayOptions: [
      ...contextData.platformsDisplayOptions,
      ...(extraContextData?.platformsDisplayOptions ?? []),
    ],
  }

  return deepPruneNull(contextData)
}

const mapCardTemplateConfigToContext = (templateConfig: TemplateConfigInterface): any => ({
  platformsDisplayOptions: _map(
    templateConfig.platformsDisplayOptions?.filter(
      (option) => !!option.flowxProps?.title || !!option.flowxProps?.subtitle
    ),
    (option) => _pick(option, ['flowxProps.title', 'flowxProps.subtitle'])
  ),
})

const mapTextTemplateConfigToContext = (templateConfig: TemplateConfigInterface): any => ({
  platformsDisplayOptions: _map(
    templateConfig.platformsDisplayOptions?.filter(
      (option) => !!option.flowxProps?.text || !!option.flowxProps?.url
    ),
    (option) => _pick(option, ['flowxProps.text', 'flowxProps.url'])
  ),
})

const mapTableTemplateConfigToContext = (templateConfig: TemplateConfigInterface): any => ({
  platformsDisplayOptions: _map(
    templateConfig.platformsDisplayOptions?.filter((option) => !!option.flowxProps?.source),
    (option) => _pick(option, ['flowxProps.source'])
  ),
})

const mapMessageTemplateConfigToContext = (templateConfig: TemplateConfigInterface): any => ({
  platformsDisplayOptions: _map(
    templateConfig.platformsDisplayOptions?.filter((option) => !!option.flowxProps?.message),
    (option) => _pick(option, ['flowxProps.message', 'flowxProps.messageType'])
  ),
})

const mapCollectionTemplateConfigToContext = (templateConfig: TemplateConfigInterface): any => ({
  platformsDisplayOptions: _map(
    templateConfig.platformsDisplayOptions?.filter((option) => !!option.flowxProps?.source),
    (option) => _pick(option, ['flowxProps.source'])
  ),
})

const componentMappers = {
  [ComponentIdentifierEnum.CARD]: mapCardTemplateConfigToContext,
  [ComponentIdentifierEnum.TEXT]: mapTextTemplateConfigToContext,
  [ComponentIdentifierEnum.TABLE]: mapTableTemplateConfigToContext,
  [ComponentIdentifierEnum.MESSAGE]: mapMessageTemplateConfigToContext,
  [ComponentIdentifierEnum.COLLECTION]: mapCollectionTemplateConfigToContext,
}

/**
 * Checks if a message has a valid execution plan
 * A valid execution plan is one that:
 * 1. Has actions defined
 * 2. Is not a single N/A action
 * 3. Does not contain NoopStore actions
 */
export const messageHasValidExecutionPlan = (message: Message): boolean => {
  const executionPlan = message.executionPlan
  if (isEmptyExecutionPlan(executionPlan)) {
    return false
  }

  // If there's only one action and it's N/A, then it's not a valid plan
  if (
    executionPlan.actions.length === 1 &&
    executionPlan.actions[0].agent?.action === AgentAction.NotAvailableAgent &&
    executionPlan.actions[0].agent?.agent === AgentName.NotAvailableAgent
  ) {
    return false
  }

  return !executionPlan.actions.some((action) =>
    [AgentAction.NoopStore, AgentAction.NotAvailableAgent].includes(
      action.agent?.action as AgentAction
    )
  )
}

/**
 * Compares two execution plans and returns the differences
 * @param currentActions The actions from the current/previous execution plan
 * @param newActions The actions from the new execution plan to compare against
 * @returns ActionsDiff containing added, updated, and deleted actions
 */
export const getExecutionPlanEnhanceDiff = (
  currentActions: ExecutionPlan['actions'],
  newActions: ExecutionPlan['actions']
): Omit<ActionsDiff, 'displayTitle'> => {
  // Create maps for easier access
  const currentActionsMap = new Map(
    currentActions.filter((action) => action.agent).map((action) => [action.localId, action])
  )
  const newActionsMap = new Map(
    newActions.filter((action) => action.agent).map((action) => [action.localId, action])
  )

  // Step 1: Detect the diff (store only IDs for each category)
  const addedIds: string[] = []
  const updatedIds: string[] = []
  const deletedIds: string[] = []

  // Find added and updated actions
  newActionsMap.forEach((newAction, actionId) => {
    const currentAction = currentActionsMap.get(actionId)
    if (!currentAction) {
      addedIds.push(actionId)
    } else if (
      currentAction.name !== newAction.name ||
      currentAction.description !== newAction.description ||
      currentAction.recommendedPrompt !== newAction.recommendedPrompt ||
      getActionCategory(currentAction.agent) !== getActionCategory(newAction.agent)
    ) {
      updatedIds.push(actionId)
    }
  })

  // Find deleted actions
  currentActionsMap.forEach((_, actionId) => {
    if (!newActionsMap.has(actionId)) {
      deletedIds.push(actionId)
    }
  })

  // Step 2: Map the IDs to ActionDiff format
  const mapActionToActionDiff = (actionId: string, actionsMap: Map<string, any>): Action => {
    const action = actionsMap.get(actionId)
    return {
      id: action.localId,
      title: action.name || '',
      description: action.description || '',
      category: getActionCategory(action.agent),
    }
  }

  return {
    added: addedIds.map((id) => mapActionToActionDiff(id, newActionsMap)),
    updated: updatedIds.map((id) => mapActionToActionDiff(id, newActionsMap)),
    deleted: deletedIds.map((id) => mapActionToActionDiff(id, currentActionsMap)),
  }
}

/**
 * Helper function to determine action category based on agent name
 */
const getActionCategory = (
  agent: ActionAgent
): 'process' | 'ui' | 'business-logic' | 'expression' => {
  if (!agent) {
    return 'process'
  }

  switch (agent.agent) {
    case AgentName.AiAnalyst:
      return 'process'
    case AgentName.AiDesigner:
      return 'ui'
    case AgentName.AiDeveloper:
      return [AgentAction.BusinessRule, AgentAction.EditBusinessRule].includes(
        agent.action as AgentAction
      )
        ? 'business-logic'
        : 'expression'
    default:
      return 'process'
  }
}

/**
 * Assigns new targets from the new execution plan to matching actions in the current execution plan
 * @param currentExecutionPlan The current execution plan to update
 * @param newExecutionPlan The new execution plan containing updated targets
 * @returns Object containing the updated execution plan and a set of updated action IDs
 */
export const assignNewTargetsToCurrentExecutionPlan = (
  currentExecutionPlan: ExecutionPlan,
  newExecutionPlan: ExecutionPlan
): { executionPlan: ExecutionPlan; updatedActions: Set<string> } => {
  const updatedActions = new Set<string>()

  // Create a map of new actions by localId for efficient lookup
  const newActionsMap = new Map(newExecutionPlan.actions.map((action) => [action.localId, action]))

  const updatedExecutionPlan = produce(currentExecutionPlan, (draft) => {
    draft.actions.forEach((currentAction) => {
      const newAction = newActionsMap.get(currentAction.localId)

      // If action exists in new plan and target is different
      if (newAction && !_isEqual(currentAction.target, newAction.target)) {
        updatedActions.add(currentAction.localId)
        currentAction.target = newAction.target
      }
    })
  })

  return {
    executionPlan: updatedExecutionPlan,
    updatedActions,
  }
}

/**
 * Applies selected changes from a new execution plan to an old execution plan
 * @param oldExecutionPlan The original execution plan to update
 * @param newExecutionPlan The new execution plan with potential changes
 * @param selectedChanges The changes that the user selected to apply
 * @returns A new execution plan with selected changes applied
 */
export const applyExecutionPlanChanges = (
  oldExecutionPlan: ExecutionPlan,
  newExecutionPlan: ExecutionPlan,
  selectedChanges: SerializedSelectedActions
): ExecutionPlan => {
  const oldActions = oldExecutionPlan.actions || []
  const newActions = newExecutionPlan.actions || []

  const newActionsMap = new Map(newActions.map((action) => [action.localId, action]))

  const resultActions = [...oldActions]

  selectedChanges.deleted.forEach((actionId) => {
    const index = resultActions.findIndex((action) => action.localId === actionId)
    if (index !== -1) {
      resultActions.splice(index, 1)
    }
  })

  selectedChanges.updated.forEach((actionId) => {
    const newAction = newActionsMap.get(actionId)
    if (newAction) {
      const index = resultActions.findIndex((action) => action.localId === actionId)
      if (index !== -1) {
        resultActions[index] = newAction
      }
    }
  })

  selectedChanges.added.forEach((actionId) => {
    const newAction = newActionsMap.get(actionId)
    if (newAction) {
      resultActions.push(newAction)
    }
  })

  // Create execution plan with applied changes and then apply targets
  const executionPlanWithChanges = {
    ...oldExecutionPlan,
    actions: resultActions,
  }

  // Always apply target property from new execution plan to all matching actions
  return assignNewTargetsToCurrentExecutionPlan(executionPlanWithChanges, newExecutionPlan)
    .executionPlan
}

export function getFileContextKey(agent: AgentName): ContextDataKeys {
  return agent === AgentName.AiAnalyst
    ? ContextDataKeys.ProcessReferenceImage
    : ContextDataKeys.DesignerReferenceImage
}

// A binary object reference is identified by `docID` once persisted, or by
// `tempFileId` while it's still a freshly-uploaded temp file (e.g. an image
// source set on an action from the plan). Either resolves the same download.
export function getBinaryObjectFileId(item: ContextItem): string | undefined {
  return item.valueBinaryObject?.docID ?? item.valueBinaryObject?.tempFileId
}

export function getFileIdsFromExectutionPlanActions(actions: ExecutionPlanAction[]): string[] {
  return _uniq(
    actions.flatMap((action) =>
      (action.context?.items ?? []).map(getBinaryObjectFileId).filter((id): id is string => !!id)
    )
  )
}

export function getActionFileId(action: Pick<ExecutionPlanAction, 'context'>): string | undefined {
  return (action.context?.items ?? []).map(getBinaryObjectFileId).find((id) => !!id)
}

export function getActionFileName(
  action: Pick<ExecutionPlanAction, 'context'>
): string | undefined {
  return (action.context?.items ?? [])
    .map((item) => item.valueBinaryObject?.fileName)
    .find((name) => !!name)
}

export function getAvailableUploadOptions(agent: AgentName): {
  imageUpload: boolean
  bpmnUpload: boolean
  documentUpload: boolean
} {
  return {
    imageUpload: [AgentName.AiAnalyst, AgentName.AiDesigner].includes(agent),
    bpmnUpload: agent === AgentName.AiAnalyst,
    documentUpload: true,
  }
}

/**
 * Checks if an execution plan action target is complete (has all required fields for immediate navigation)
 * or partial (only has node ID and needs user to complete selection in chat).
 *
 * @param agent - The agent information (agent type and action)
 * @param target - The execution plan action target
 * @returns true if target has all required fields for navigation, false if it needs completion
 */
export function isTargetComplete(agent: ActionAgent, target: ActionTarget): boolean {
  if (!target || target.targetType == null) {
    return false
  }

  // AiAnalyst always has complete targets (process level)
  if (agent.agent === AgentName.AiAnalyst) {
    return true
  }

  // AiDesigner only needs a node ID (navigationAreaId will be looked up from the node)
  if (agent.agent === AgentName.AiDesigner) {
    return !!target.node?.id
  }

  // AiDeveloper requires specific fields based on action type
  if (agent.agent === AgentName.AiDeveloper) {
    // Business rules require nodeId, actionId, and index
    if (
      [AgentAction.BusinessRule, AgentAction.EditBusinessRule].includes(agent.action as AgentAction)
    ) {
      // Check if we have businessRule structure with all required fields
      if (
        target.businessRule?.nodeId &&
        target.businessRule?.actionId &&
        target.businessRule?.index !== null &&
        target.businessRule?.index !== undefined
      ) {
        return true
      }
      // If targetType is NODE with only node.id, it's partial
      return false
    }

    // Expressions require nodeId and elementId
    if (agent.action === AgentAction.Expression) {
      // Check if we have expression structure with all required fields
      if (target.expression?.nodeId && target.expression?.elementId) {
        return true
      }
      // If targetType is NODE with only node.id, it's partial
      return false
    }
  }

  return false
}

/**
 * Determines the outcome type for Developer agent based on the action type
 * @param action The agent action being performed
 * @param activeAgentThread The active agent thread (used for context lookup in improve/document/fix actions)
 * @returns The outcome type or null if not determinable
 */
export function getDeveloperOutcomeType(
  action: string,
  activeAgentThread: ConversationThread | null
): OutcomeType | null {
  // Direct mapping for specific actions
  if ([AgentAction.BusinessRule, AgentAction.EditBusinessRule].includes(action as AgentAction)) {
    return OutcomeType.BusinessRule
  }
  if (action === AgentAction.Expression) {
    return OutcomeType.Expression
  }
  if (action === AgentAction.ComputedValue) {
    return OutcomeType.ComputedValue
  }
  if (action === AgentAction.CustomComponent) {
    return OutcomeType.CustomComponent
  }

  // For ImproveCode, DocumentCode, FixCode - read from context
  if (
    [AgentAction.ImproveCode, AgentAction.DocumentCode, AgentAction.FixCode].includes(
      action as AgentAction
    )
  ) {
    const firstMessage = activeAgentThread?.messages?.[0]
    const contentTypeItem = firstMessage?.contextData?.items?.find(
      (item) => item.key === ContextDataKeys.UiAiContentType
    )
    const contentType = contentTypeItem?.valueGenericString

    // The stored AIContentType values match OutcomeType enum values exactly
    return (contentType as OutcomeType) ?? OutcomeType.BusinessRule
  }

  return null
}

export function shouldAutoStartExecutionPlan(executionPlan: ExecutionPlan): boolean {
  if (executionPlan.actions.length !== 1) {
    return false
  }
  const action = executionPlan.actions[0]
  return action.agent?.agent === AgentName.AiAssistant
}

const ONE_SHOT_AGENT_ACTIONS = new Set<string>([
  `${AgentName.AiAssistant}:${AgentAction.AskAssistant}`,
  `${AgentName.AiDeveloper}:${AgentAction.ExplainCode}`,
])

export const isOneShotAgentAction = (agent?: ActionAgent | null): boolean =>
  !!agent && ONE_SHOT_AGENT_ACTIONS.has(`${agent.agent}:${agent.action}`)
